import { useEffect } from 'react';

/**
 * 스크롤 리빌 공용 훅 — AI30 랜딩(/events/2nd-semester-ai)에서 검증된 패턴.
 *
 * 대상 요소가 뷰포트에 들어오면 클래스를 한 번 부여하고 관찰을 해제한다.
 * - threshold 0.16 + rootMargin 하단 -8%: 요소가 살짝 올라온 뒤 발화해 "따라오는" 느낌을 준다
 * - IntersectionObserver 미지원(구형 브라우저·jsdom 테스트): 전부 즉시 표시
 * - prefers-reduced-motion: 애니메이션 트리거 자체를 생략하고 즉시 표시
 *   (CSS 쪽 @media prefers-reduced-motion 무효화와 이중 안전망)
 *
 * 앱 셸은 body가 아니라 .content-wrapper로 스크롤하지만, IO의 기본 root(뷰포트)는
 * 중첩 스크롤 컨테이너와 무관하게 교차를 판정하므로 별도 처리가 필요 없다.
 *
 * @param {React.RefObject|null} rootRef 관찰 범위 루트(없으면 document)
 * @param {string} selector 리빌 대상 셀렉터
 * @param {string} inClass 발화 시 부여할 클래스
 */
const useScrollReveal = (rootRef, selector, inClass = 'in') => {
  useEffect(() => {
    const root = rootRef ? rootRef.current : document;
    if (!root) return undefined;
    const targets = root.querySelectorAll(selector);
    if (!targets.length) return undefined;

    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (typeof IntersectionObserver === 'undefined' || reduceMotion) {
      targets.forEach((el) => el.classList.add(inClass));
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(inClass);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [rootRef, selector, inClass]);
};

export default useScrollReveal;
